import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { Security } from '../../proto/generated/security'
import websocketActions from '../../actions/websocket.actions'
import { subscriptionTags } from '../../assets/constants'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { selectOrdersHistory } from '../../selectors/orders'
import { RootState } from '../../store'
import Loading from '../common/Loading'
import { WidgetListItems } from '../../util/sidebar'
import { selectRightSidebarWidget } from '../../selectors/common'
import { selectQuickWidgetConfig } from '../../selectors/quickWidgets'
import { QuickWidgetTypes } from '../../reducer/user.reducer'
import ReconnectView from '../common/ReconnectView'
import ReconnectTradingAccount from '../common/ReconnectTradingAccount'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { Order, OrderStatus } from '../../proto/generated/order'
import { fetchBrokerPositionsAndOrders } from '../../services/apis/tradingAccount'
import { isBrokerSessionReady, shouldShowTradingAccountReconnect } from '../../util/orderPlacement'
import { isShoonyaBackend } from '../../util/backendProvider'
import EmptyView from './EmptyView'
import GenericFilterTabs from '../genericTabs/genericFilterTabs'
import PositionsSidebarWidget from '../positionsSidebarWidget/positionsSidebarWidget'
import { selectPositions } from '../../selectors/positions'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { filterOrdersOpenTabWide } from '../../util/icebergOrders'
import OrdersPanelHeader from '../ordersWidget/OrdersPanelHeader'
import OrdersTableScrollArea from '../ordersWidget/OrdersTableScrollArea'

export enum PositionNavButtons {
    PNL = 'PNL',
    GREEKS = 'GREEKS'
}

export const navItems = [
    {
        key: PositionNavButtons.PNL,
        label: 'P/L'

    },
    {
        key: PositionNavButtons.GREEKS,
        label: 'Greeks'
    }
]

export enum OrdersNavButtons {
    OPEN = 'OPEN',
    ALL = 'ALL'
}

export enum OrdersNavButtonsDetailed {
    OPEN = 'OPEN',
    COMPLETE = 'COMPLETE',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    ALL = 'ALL',
}

export const positionsNavItems = [
    {
        key: OrdersNavButtons.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtons.ALL,
        label: 'All'
    }
]

export const ordersNavItems = [
    {
        key: OrdersNavButtonsDetailed.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtonsDetailed.COMPLETE,
        label: 'Complete'
    },
    {
        key: OrdersNavButtonsDetailed.REJECTED,
        label: 'Rejected'
    },
    {
        key: OrdersNavButtonsDetailed.CANCELLED,
        label: 'Cancelled'
    },
    {
        key: OrdersNavButtonsDetailed.ALL,
        label: 'All'
    },
]

export interface SelectedSqOffStrategyType {
    underlyingSecurity: Security | undefined
    strategyCode: string | undefined
    basketId: string | undefined
    strategyName: string | undefined
    index: number,
    analyzeAndAdjust?: boolean
}

const PositionsWidget = () => {
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const [showContent, setShowContent] = useState(false)
    const [showReconnect, setShowReconnect] = useState(false);
    const [tableViewType, setTableViewType] = useState<PositionNavButtons>(PositionNavButtons.PNL)
    const isDragging = useRef(false)
    const [ordersTableViewType, setOrdersTableViewType] = useState<OrdersNavButtonsDetailed>(OrdersNavButtonsDetailed.OPEN)
    const [searchText, setSearchText] = useState('')
    const [showSearch, setShowSearch] = useState(false)
    const [filterOrdersData, setFilterOrdersData] = useState<Order[]>([])
    const [positionsHeight, setPositionsHeight] = useState(0.6)

    const positions = useSelector(selectPositions)

    const ordersData = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const loading = useSelector((state: RootState) => state.positionsStore.loading)
    const rightSideWidget = useSelector(selectRightSidebarWidget)
    const positionWidgetConfig = useSelector(selectQuickWidgetConfig(QuickWidgetTypes.POSITION));

    const brokerSessionReady = isBrokerSessionReady(primaryTradingAccount)

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (brokerSessionReady) {
            setShowReconnect(false);
            setShowContent(true);
            if (isShoonyaBackend() && positions.length === 0) {
                void fetchBrokerPositionsAndOrders(primaryTradingAccount)
            }
        } else {
            setShowContent(false);
            timer = setTimeout(() => {
                setShowReconnect(shouldShowTradingAccountReconnect(primaryTradingAccount));
            }, 200);
        }
        return () => {
            clearTimeout(timer);
            const isQuickWidgetOpen = positionWidgetConfig?.enabled === true;
            const isSidebarOpen = rightSideWidget === WidgetListItems.POSITIONS;
            if (!isQuickWidgetOpen && !isSidebarOpen) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            securities: []
                        }
                    ],
                    candleSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            timeBucket: undefined,
                            securities: []
                        }
                    ]
                });
            }
        };
    }, [brokerSessionReady, primaryTradingAccount, dispatch, positionWidgetConfig?.enabled, rightSideWidget, positions.length]);


    const getFilteredOrders = (orders: Order[]) => {
        if (showSearch && searchText.trim().length > 0) {
            return orders;
        }
        switch (ordersTableViewType) {
            case OrdersNavButtonsDetailed.OPEN:
                return filterOrdersOpenTabWide(orders);
            case OrdersNavButtonsDetailed.COMPLETE:
                return orders.filter(order => order.status === OrderStatus.EXECUTED);
            case OrdersNavButtonsDetailed.REJECTED:
                return orders.filter(order => (order.status === OrderStatus.REJECTED || order.status === OrderStatus.FAILED));
            case OrdersNavButtonsDetailed.CANCELLED:
                return orders.filter(order => order.status === OrderStatus.CANCELLED);
            case OrdersNavButtonsDetailed.ALL:
                return orders;
            default:
                return orders;
        }
    };

    const visibleOrders = getFilteredOrders(filterOrdersData);
    
    // Loading snapshots for orders in right sidebar widget
    const securities = useMemo(()=>{
        return visibleOrders.filter(order => ![OrderStatus.REJECTED, OrderStatus.CANCELLED, OrderStatus.EXECUTED, OrderStatus.FAILED].includes(order.status)).map((order)=> order.security)
    },[visibleOrders])

    useEffect(() => {
        if (!securities || !securities.length) return;
        fetchSecuritySnapshotsBulk({ securities, dispatch });
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.ORDERS_WIDGET_SUBSCRIPTION_TAG,
                    securities
                }
            ]
        })
    }, [securities])

    const orderDataSecKeys = useMemo(() => {
        const data: string[] = []
        ordersData?.forEach((order) => {
            const secKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
            data.push(secKey)
        })
        return data
    }, [ordersData])
    useEffect(() => {
        let cancelled = false
        const run = async () => {
            if (!ordersData?.length) { setFilterOrdersData([]); return }
            const results = await Promise.all(
                ordersData.map((data, i) =>
                    getSecurityAsync(orderDataSecKeys[i]).then((security) =>
                        (security?.symbol)?.toLowerCase()?.includes(searchText.trim().toLowerCase())
                    )
                )
            )
            if (!cancelled) setFilterOrdersData(ordersData.filter((_, i) => results[i]))
        }
        run()
        return () => { cancelled = true }
    }, [orderDataSecKeys, ordersData, searchText])

    const startDragging = () => {
        isDragging.current = true;
        document.body.style.userSelect = "none";
        document.body.style.pointerEvents = "none";
        document.body.style.cursor = "ns-resize";
    };

    const stopDragging = () => {
        isDragging.current = false;
        document.body.style.userSelect = "auto";
        document.body.style.pointerEvents = "auto";
        document.body.style.cursor = "auto";
    };

    const onDrag = (e: MouseEvent) => {
        if (!isDragging.current) return;
        document.body.style.cursor = "ns-resize";
        const containerElement = document.querySelector('[data-positions-container]') as HTMLElement;
        if (containerElement) {
            const rect = containerElement.getBoundingClientRect();
            const relativeY = e.clientY - rect.top;
            const percentage = relativeY / rect.height;
            const clampedPercentage = Math.max(0.20, Math.min(0.80, percentage));
            setPositionsHeight(clampedPercentage);
        }
    };

    useEffect(() => {
        window.addEventListener("mousemove", onDrag);
        window.addEventListener("mouseup", stopDragging);
        return () => {
            window.removeEventListener("mousemove", onDrag);
            window.removeEventListener("mouseup", stopDragging);
        };
    }, [])

    return (
        <Paper
            sx={{
                borderTopRightRadius: '6px',
                display: 'flex',
                height: '100%',
                flexDirection: 'column'
            }}
        >
            {
                loading ? <Loading size={30} /> : (showContent ? (
                    <>
                        <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }} data-positions-container>
                            <Box sx={{
                                flex: positionsHeight,
                                minHeight: '200px',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <PositionsSidebarWidget positions={positions} />
                            </Box>
                            <Box
                                className="divider"
                                sx={{
                                    height: 'var(--extra-small-spacing)',
                                    cursor: 'ns-resize',
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }}
                                onMouseDown={startDragging}
                            />
                            <Box sx={{ flex: 1 - positionsHeight, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: '200px' }}>
                                <OrdersPanelHeader
                                    showSync={false}
                                    ordersTableViewType={ordersTableViewType}
                                    onOrdersTableViewTypeChange={setOrdersTableViewType}
                                    hasOrders={!!(ordersData && ordersData.length > 0)}
                                    showSearch={showSearch}
                                    onShowSearch={setShowSearch}
                                    searchText={searchText}
                                    onSearchTextChange={setSearchText}
                                />
                                <OrdersTableScrollArea
                                    orders={visibleOrders}
                                    tabType={ordersTableViewType}
                                    emptyText={
                                        searchText.trim().length > 0
                                            ? 'No orders found for your search.'
                                            : 'Your placed and executed orders will appear here.'
                                    }
                                    variant="sidebar"
                                />
                            </Box>
                        </Box>
                    </>) : (
                    showReconnect && (<>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Stack justifyContent={'center'} alignItems={'center'}>
                                <ReconnectView text={primaryTradingAccount ? "Reconnect trading account to view positions." : "Connect trading account to view positions."} />
                                <ReconnectTradingAccount primaryTradingAccount={primaryTradingAccount} />
                            </Stack>
                        </Box>
                    </>)
                )
                )
            }
        </Paper>
    )
}
export default memo(PositionsWidget);
